import type { ComponentType, ReactNode } from 'react';
import type { HostBase } from '@vnetwork/margarita-shell/host-base';

// What this module needs from the app, typed structurally: nothing here imports the app, so the
// module can move to a package. `src/app/register-fastchannel.tsx` builds it from real values and
// hands it to `createFastchannelModule(host)` (module.ts). The HTTP clients are not members: they
// come from the shared registry, `getHttpClient` of `@vnetwork/margarita-core/http`.

export interface FastchannelHost extends Pick<HostBase, 'trackFeatureAction'> {
  /** Base URL of the packager that serves each channel's HLS output (`env.fastChannelUrl`). */
  fastChannelUrl: string;
  /** Short brand name used in user-facing copy (`brand.shortName`). */
  brandShortName: string;
  /** The shared HLS preview player. A component slot: the app wraps it lazily so the player's code
   *  stays out of the entry chunk. */
  HlsPreviewPlayer: ComponentType<{
    src: string;
    title: ReactNode;
    description?: ReactNode;
    live?: boolean;
    banner?: ReactNode;
    emptyMessage?: ReactNode;
    onLatency?: (seconds: number) => void;
  }>;
}

let current: FastchannelHost | null = null;

/** Called once by `createFastchannelModule`, before any route renders. */
export function registerFastchannelHost(host: FastchannelHost): void {
  current = host;
}

/** The registered host. Throws when the module is used without `createFastchannelModule(host)`. */
export function fastchannelHost(): FastchannelHost {
  if (!current) throw new Error('Fastchannel: createFastchannelModule(host) was not called before the module rendered');
  return current;
}

/** `host.trackFeatureAction`, resolved at call time so call sites read like the shared one. */
export const trackFeatureAction: FastchannelHost['trackFeatureAction'] = (service, feature, verb, params) =>
  fastchannelHost().trackFeatureAction(service, feature, verb, params);
